import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Progress } from "./ui/progress";
import { toast } from "sonner";
import {
  BookOpen,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  initial_setup: "Initial Setup",
  topic_mentoring: "Topic Mentoring",
  activities: "Activities (Required & Encouraged)",
  ongoing: "Ongoing Requirements",
  graduation: "Graduation Requirements"
};

const CATEGORY_COLORS = {
  initial_setup: "#3B82F6",
  topic_mentoring: "#8B5CF6",
  activities: "#F59E0B",
  ongoing: "#10B981",
  graduation: "#EF4444"
};

export const TrainingTracker = ({ memberId, canEdit, primaryColor }) => {
  const [topics, setTopics] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({
    initial_setup: true,
    topic_mentoring: true,
    activities: true,
    ongoing: true,
    graduation: true
  });
  const [updating, setUpdating] = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, [memberId]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [topicsRes, progressRes] = await Promise.all([
        fetch(`${API}/training-topics`, { headers }),
        fetch(`${API}/members/${memberId}/training`, { headers })
      ]);

      if (topicsRes.ok) {
        const topicsData = await topicsRes.json();
        setTopics(topicsData);
        // Auto-seed if no topics
        if (topicsData.length === 0) {
          await seedTopics();
        }
      }
      if (progressRes.ok) {
        const progressData = await progressRes.json();
        const progressMap = {};
        progressData.forEach(p => {
          progressMap[p.topic_id] = p;
        });
        setProgress(progressMap);
      }
    } catch (error) {
      console.error("Training fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const seedTopics = async () => {
    try {
      const res = await fetch(`${API}/training-topics/seed`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const topicsRes = await fetch(`${API}/training-topics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (topicsRes.ok) {
          setTopics(await topicsRes.json());
        }
      }
    } catch (error) {
      console.error("Seed topics error:", error);
    }
  };

  const toggleCompletion = async (topicId, currentState) => {
    if (!canEdit) return;
    setUpdating(topicId);
    try {
      const res = await fetch(`${API}/members/${memberId}/training/${topicId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ completed: !currentState })
      });

      if (res.ok) {
        const updated = await res.json();
        setProgress(prev => ({ ...prev, [topicId]: updated }));
        toast.success(!currentState ? "Training marked complete" : "Training marked incomplete");
      } else {
        toast.error("Failed to update training status");
      }
    } catch (error) {
      toast.error("Error updating training");
    } finally {
      setUpdating(null);
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Group topics by category
  const groupedTopics = {};
  topics.forEach(t => {
    if (!groupedTopics[t.category]) groupedTopics[t.category] = [];
    groupedTopics[t.category].push(t);
  });

  // Calculate overall progress
  const completedCount = Object.values(progress).filter(p => p.completed).length;
  const totalCount = topics.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (topics.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-display flex items-center gap-2">
            <GraduationCap className="h-5 w-5" style={{ color: primaryColor }} />
            Training Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-4">
            No training topics configured yet.
          </p>
          {canEdit && (
            <div className="text-center">
              <Button variant="outline" onClick={seedTopics} data-testid="seed-training-btn">
                <BookOpen className="mr-2 h-4 w-4" />
                Initialize Training Topics
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm" data-testid="training-tracker">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-display flex items-center gap-2">
            <GraduationCap className="h-5 w-5" style={{ color: primaryColor }} />
            Training Progress
          </CardTitle>
          <Badge
            variant="outline"
            className="text-sm"
            data-testid="training-progress-badge"
          >
            {completedCount}/{totalCount} Complete
          </Badge>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-muted-foreground">Overall Progress</span>
            <span className="font-medium">{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(CATEGORY_LABELS).map(([catKey, catLabel]) => {
          const catTopics = groupedTopics[catKey] || [];
          if (catTopics.length === 0) return null;
          const catCompleted = catTopics.filter(t => progress[t.topic_id]?.completed).length;
          const isExpanded = expandedCategories[catKey];
          const catColor = CATEGORY_COLORS[catKey];

          return (
            <div key={catKey} className="border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleCategory(catKey)}
                className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
                data-testid={`training-category-${catKey}`}
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: catColor }} />
                  <span className="font-medium text-sm">{catLabel}</span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {catCompleted}/{catTopics.length}
                </Badge>
              </button>

              {isExpanded && (
                <div className="border-t divide-y">
                  {catTopics.map(topic => {
                    const isCompleted = progress[topic.topic_id]?.completed;
                    const isUpdating = updating === topic.topic_id;
                    return (
                      <div
                        key={topic.topic_id}
                        className={`flex items-start gap-3 p-3 pl-10 transition-colors ${
                          isCompleted ? "bg-green-50/50" : "hover:bg-slate-50"
                        }`}
                        data-testid={`training-topic-${topic.code}`}
                      >
                        {canEdit ? (
                          <button
                            onClick={() => toggleCompletion(topic.topic_id, isCompleted)}
                            disabled={isUpdating}
                            className="mt-0.5 flex-shrink-0"
                            data-testid={`training-toggle-${topic.code}`}
                          >
                            {isUpdating ? (
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            ) : isCompleted ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            ) : (
                              <Circle className="h-5 w-5 text-slate-300" />
                            )}
                          </button>
                        ) : (
                          <div className="mt-0.5 flex-shrink-0">
                            {isCompleted ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            ) : (
                              <Circle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-muted-foreground">{topic.code}</span>
                            <span className={`text-sm ${isCompleted ? "line-through text-muted-foreground" : "text-slate-700"}`}>
                              {topic.name}
                            </span>
                          </div>
                          {topic.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{topic.description}</p>
                          )}
                          {isCompleted && progress[topic.topic_id]?.completed_at && (
                            <p className="text-xs text-green-600 mt-0.5">
                              Completed {progress[topic.topic_id].completed_at?.split("T")[0]}
                              {progress[topic.topic_id].completed_by && ` by ${progress[topic.topic_id].completed_by}`}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default TrainingTracker;
